import React, { useState, useCallback } from "react";
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    FormTitle,
    FormText,
    Container
} from 'screens/Authentication/CreatePassword/styled';

import Button from "components/Form/Button";
import Input from 'components/Form/Input';

import ContainerUnauthenticated from "containers/Unauthenticated";
import { DoMVUEssentialsResetPassword } from "services/authentication";
import { exposeStrapiError } from "utils";
import { UnauthenticatedContainer } from "ui/styled";
import PasswordValidation from "components/Form/PasswordValidation";
import FormSuccess from "components/FormSuccess";
import useI18n from "hooks/useI18n";

export default function MVUEssentialsCreatePassword() {
    const history = useHistory();
    const navigate = useCallback(to => history.push(`/${to}`), [history]);

    const params = new URLSearchParams(window.location.search);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const { t } = useI18n();

    const [form, setForm] = useState({});
    const formValue = useCallback(ref => (form?.[ref] ? form?.[ref] : ''), [form]);
    const changeForm = useCallback((value, ref) => { setForm({ ...form, [ref]: value }); }, [form]);

    const valid = useCallback((verbose = false) => {
        if (!formValue('password') || !formValue('password').length) {
            if (verbose) { toast.error(`${t("fill_the_field")}: ${t("new_password")}`); }
            return false;
        }

        if (!formValue('cpassword') || !formValue('cpassword').length) {
            if (verbose) { toast.error(`${t("fill_the_field")}: ${t("confirm_new_password")}`); }
            return false;
        }

        if (formValue('password') !== formValue('cpassword')) {
            if (verbose) { toast.error(t("new_password_and_new_password_confirmation_are_not_the_same")); }
            return false;
        }
        if (!formValue('password')?.match(/[A-Z]/)) {
            toast.error(t('one_uppercase_letter'));
            return false;
        }

        if (!formValue('password')?.match(/[0-9]/)) {
            toast.error(t('one_number'));
            return false;
        }
        if (formValue('password')?.length < 6) {
            toast.error(t('at_least_6_characters'));
            return false;
        }
        if (!formValue('password')?.match(/[!@#$%^&*()\-\+=\{\}\[\]\\|;:,.\/?'\"~<>`´]/)) {
            toast.error(t('at_least_1_special_character'));
            return false;
        }
        return true;
    }, [t, formValue]);

    const action = async () => {
        if (!valid(true)) { return; }
        setLoading(true);

        const result = await DoMVUEssentialsResetPassword({
            code: params.get('code'),
            password: formValue('password'),
            passwordConfirmation: formValue('cpassword'),
        });

        setLoading(false);
        if (result && !exposeStrapiError(result)) {
            completeNext();
        }
    };

    const completeNext = useCallback(() => {
        setSuccess({
            title: t('success'),
            text: t("password_changed_successfully"),
            label: t('close'),
            action: () => navigate('mvu-lite/login'),
        });
    }, [t, navigate]);

    return (
        <>
            {!success ? null : <FormSuccess success={success} />}
            {success ? null : (
                <ContainerUnauthenticated lightBlue>
                    <UnauthenticatedContainer>
                        <Container>
                            <FormTitle>{t("create_a_new_password")}</FormTitle>
                            <FormText>{t("enter_a_new_password_for_your_account")}</FormText>
                        </Container>
                        <Input
                            id={'password'}
                            placeholder={t("new_password")}
                            type="password"
                            value={formValue('password')}
                            onChange={e => changeForm(e.target.value, 'password')}
                        />
                        <Input
                            id={'cpassword'}
                            placeholder={t("confirm_new_password")}
                            type="password"
                            value={formValue('cpassword')}
                            onChange={e => changeForm(e.target.value, 'cpassword')}
                        />
                        <PasswordValidation password={formValue('password')} />
                        <Button color="blue" loading={loading} onClick={action}>{t("save")}</Button>
                    </UnauthenticatedContainer>
                </ContainerUnauthenticated>
            )}
        </>
    );
}
